import { Dispatch, SetStateAction, useEffect, useState, useCallback, useMemo } from 'react'
import { Loader } from '@googlemaps/js-api-loader'
import { MarkerClusterer, MarkerClustererOptions } from '@googlemaps/markerclusterer'
import {
    AssetType,
    BucketDataType,
    LocationHistoryType,
    SelectedPingType,
    TagType,
} from '../../types/bucketTypes'
import { getImgSrc } from '../../helpers/getImageSrc'
import { ASSET_DATA } from '../../constants/bucket.contants'
import { darkModeStyle, routePath } from '../../constants/navigation.constants'
import { useDarkMode } from '../../hooks/use-darkmode'
import { setSelectedAssets, setTagInfo } from '../../redux/actions'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { logError, logInfo } from '../../services/logger.service'
import { useGlobalError } from '../../contexts/global-error.context'
import './map.scss';
import { LoadingSpinner } from '../loading-spinner'

interface Props {
    selectedAsset?: any
    bucketList?: AssetType[]
    setIsModalShown?: Dispatch<SetStateAction<boolean>>
    isModalShown?: boolean
    setSelectedAsset?: Dispatch<SetStateAction<BucketDataType>>
    tagData?: TagType
    isLocationHistory?: boolean
    locationHistory?: LocationHistoryType[]
    selectedPing?: SelectedPingType
    getTagData?: any
    setTagData?: Dispatch<any>
    zoom?: number
    searchCenter?: {lat: number, lng: number} | null
}
const saveStateToLocalStorage = (key: string, state: any, showError: (msg: string) => void): void => {
    const serializedState = JSON.stringify(state);
    localStorage.setItem(key, serializedState);
};
const DEFAULT_MAP_ZOOM = 5; // Default zoom level for the map
export function DisplayMap(props: Readonly<Props>) {
    const { showError } = useGlobalError();
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const {
        bucketList,
        setIsModalShown,
        setSelectedAsset,
        tagData,
        isLocationHistory,
        locationHistory,
        selectedPing,
        isModalShown,
        getTagData,
        setTagData,
        searchCenter,
        zoom
    } = props;

    const [userLocation, setUserLocation] = useState<{ lat: number, lng: number } | null>(null);
    
    // Memoize the loader to prevent recreation on every render
    const loader = useMemo(() => new Loader({
        apiKey: process.env.REACT_APP_MAPS_API_KEY as string,
    }), []);

    const requestGeolocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setUserLocation({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    });
                },
                (error) => {
                    switch (error.code) {
                        case error.PERMISSION_DENIED:
                            console.warn("User denied the request for Geolocation.");
                            break;
                        case error.POSITION_UNAVAILABLE:
                            console.warn("Location information is unavailable.");
                            break;
                        case error.TIMEOUT:
                            console.warn("The request to get user location timed out.");
                            break;
                        default:
                            console.warn("An unknown error occurred.");
                            break;
                    }
                    // Fallback to a default location if geolocation fails
                    setUserLocation({
                        lat: 38.7946,
                        lng: -95.7129,
                      });
                },
                {
                    enableHighAccuracy: false, // Request approximate location
                    timeout: 5000, // 5 seconds timeout
                    maximumAge: 0,
                }
            );
        } else{
            setUserLocation({
                lat: 38.7946,
                lng: -95.7129,
              });
        }
    };
    useEffect(() => {
        requestGeolocation();
    }, []);

    const {darkMode} = useDarkMode()

    const handleMarkerClick = (buckets: AssetType) => {
        sessionStorage.setItem(ASSET_DATA, JSON.stringify(buckets));
        if (setSelectedAsset && setIsModalShown) {
            if (getTagData && setTagData) {
                let tagInfo = getTagData(buckets);
                setTagData?.(tagInfo);
                dispatch(setTagInfo(tagInfo));
                saveStateToLocalStorage('tagInfo', tagInfo, showError);
            }
            setSelectedAsset(buckets.bucket);
            dispatch(setSelectedAssets(buckets.bucket));
            navigate(routePath.ASSET_DETAIL);
            saveStateToLocalStorage('selectedAsset', buckets.bucket, showError);
        }
    };

    const getDefaultCenter = () => ({
        lat: 37.0902,
        lng: -95.7129,
    });

    const getCenterFromAssets = (assets: any) => {
        if (!tagData || !assets || assets.length === 0) {
            return null;
        }

        const firstAsset = assets[0];
        if (locationHistory) {
            return firstAsset?.latitude && firstAsset?.longitude
                ? { lat: firstAsset.latitude, lng: firstAsset.longitude }
                : null;
        }

        return firstAsset?.location?.latitude && firstAsset?.location?.longitude
            ? { lat: firstAsset.location.latitude, lng: firstAsset.location.longitude }
            : null;
    };

    const getMapCenter = (assets?: any) => {
        // Priority 1: Search center (when searching)
        if (searchCenter) {
            return searchCenter;
        }
        
        // Priority 2: Selected ping
        if (selectedPing) {
            return {
                lat: selectedPing.lat,
                lng: selectedPing.lng,
            };
        }
        
        // Priority 3: Center from assets
        const assetCenter = getCenterFromAssets(assets);
        if (assetCenter) {
            return assetCenter;
        }
        
        // Priority 4: User location or default
        return userLocation || getDefaultCenter();
    };

    const getAssets = () => {
        if (tagData) {
            if (locationHistory) return locationHistory;
            return [tagData];
        }
        if (bucketList) return bucketList;
        
        return [];
    };

    // Memoize map element ID to avoid recalculation
    const mapElementId = useCallback(() => {
        if (tagData) {
            return isLocationHistory
                ? 'location-history-map'
                : 'locations-tab-map';
        }
        return 'map';
    }, [tagData, isLocationHistory]);
    const initializeMap = (
        mapElementId: string,
        mapCenter: google.maps.LatLngLiteral,
        darkMode: boolean,
        zoomLevel?: number
    ): google.maps.Map | null => {
        const mapElement = document.getElementById(mapElementId);
        if (!mapElement) {
            logError(`Map element with id '${mapElementId}' not found`, undefined, 'DisplayMap.initializeMap');
            return null;
        }
        
        return new google.maps.Map(mapElement, {
            center: mapCenter,
            styles: darkMode? darkModeStyle :[],
            zoom: zoomLevel || DEFAULT_MAP_ZOOM,
        });
    };
    // Helper to get the correct bucket/location
    const getBucketLocation = (buckets: any): any => {
        if (bucketList) {
            return buckets?.tag?.location;
        } else if (tagData) {
            if (locationHistory) {
                return buckets;
            } else {
                return buckets?.location;
            }
        }
        return undefined;
    };

    // Helper to build marker options
    const buildMarkerOptions = (bucket: any, buckets: any): google.maps.MarkerOptions => {
        const position = {
            lat: bucket?.latitude,
            lng: bucket?.longitude,
        };
        const isSelected =
            selectedPing &&
            bucket?.latitude === selectedPing.lat &&
            bucket?.longitude === selectedPing.lng;

        const markerOptions: google.maps.MarkerOptions = { position };

        if (!isSelected) {
            // For location history, always use parent tagData for marker type
            let iconTagData;
            if (isLocationHistory) {
                iconTagData = tagData;
            } else {
                iconTagData = bucketList ? buckets.tag : buckets;
            }
            markerOptions.icon = {
                url: getImgSrc(iconTagData, true),
                scaledSize: new google.maps.Size(25, 30),
            };
        }
        return markerOptions;
    };

    // Helper for marker click
    const markerClickHandler = (buckets: any) => {
        if (
            bucketList &&
            (!tagData || tagData === undefined || bucketList.length > 0)
        ) {
            handleMarkerClick(buckets);
        }
    };

    const addMarkersToMap = (map: google.maps.Map, assets: any[]): void => {
        const markers = assets.map((buckets: any) => {
            const bucket = getBucketLocation(buckets);
            if (bucket?.latitude && bucket?.longitude) {
                const markerOptions = buildMarkerOptions(bucket, buckets);
                const marker = new google.maps.Marker(markerOptions);
                marker.addListener('click', () => markerClickHandler(buckets));
                return marker;
            }
            return undefined;
        }).filter((marker: any): marker is google.maps.Marker => marker !== undefined);
        
        // Configure clustering based on navigation state and zoom level
        const clusterConfig: MarkerClustererOptions = { map, markers };

        const markerClusterer = new MarkerClusterer(clusterConfig);
        logInfo(`Marker clusterer created ${markerClusterer}`);
    };
    const loadMap = useCallback(() => {
        const assetList = getAssets();
        const elementId = mapElementId();
        const centerParams = tagData ? assetList : null;
        const center = getMapCenter(centerParams);
        const zoomLevel = zoom || DEFAULT_MAP_ZOOM; // Use zoom if available, otherwise default
        loader.load().then(() => {
            const map = initializeMap(elementId, center, darkMode, zoomLevel);
            if (map && assetList.length > 0) {
                addMarkersToMap(map, assetList);
            }
        }).catch((error: any) => {
            logError('Failed to load Google Maps', error, 'DisplayMap.loadMap');
        });
    }, [tagData, locationHistory, bucketList, searchCenter, zoom, darkMode, selectedPing, userLocation, mapElementId]);
  
    // Optimized effect - only re-run when loadMap function changes
    useEffect(() => {
        loadMap();
    }, [loadMap]);
    const getMapStyle = () => {
        if (isLocationHistory) {
            return { height: '600px', width: '100%' };
        } else {
            return { height: '300px', width: '100%' };
        }
    };
    const renderMap = () => {
         if(bucketList){
            return (
                <div
                    className={isModalShown ? 'disable-pointer' : ''}
                    id="map"
                    style={{ height: '100%' }}
                />
            );
        }
        if (tagData) {
            return (
                <div style={{ position: 'relative', ...getMapStyle() }}>
                    <div
                        id={
                            isLocationHistory
                                ? 'location-history-map'
                                : 'locations-tab-map'
                        }
                        data-testid={
                            isLocationHistory
                                ? 'location-history-map'
                                : 'locations-tab-map'
                        }
                        style={{ width: '100%', height: '100%' }}
                    />
                    
                </div>
            );
        }else{
            return (
                <LoadingSpinner darkMode={darkMode} />
            );
        }
    };

    return <>
    {renderMap()}
    </>;
}